import { useState } from 'react';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@artstorefronts/ui';
import { X, Tag } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { ASSIGN_MOCK_FILTER, UNASSIGN_MOCK_FILTER } from '../graphql/mutations';

interface MockFilter {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface MockFiltersCellProps {
  mockId: string;
  mockFilters: MockFilter[];
  availableFilters: MockFilter[];
  isDeleted: boolean;
  onRefetch: () => void;
}

function MockFiltersCell({ mockId, mockFilters, availableFilters, isDeleted, onRefetch }: MockFiltersCellProps) {
  const { t } = useTranslation('artwork');
  const [selectedFilterToAssign, setSelectedFilterToAssign] = useState<string>('');

  const [assignMockFilter] = useMutation(ASSIGN_MOCK_FILTER);
  const [unassignMockFilter] = useMutation(UNASSIGN_MOCK_FILTER);

  const handleAssignFilter = async (filterId: string) => {
    try {
      await assignMockFilter({
        variables: {
          input: { mockId, mockFilterId: filterId },
        },
      });

      toast.success(t('mockups.filter-assigned-success'));
      await onRefetch();
    } catch (error) {
      console.error('Error assigning filter:', error);
      toast.error(t('mockups.filter-assigned-error'));
    }
  };

  const handleUnassignFilter = async (filterId: string) => {
    try {
      await unassignMockFilter({
        variables: {
          input: { mockId, mockFilterId: filterId },
        },
      });

      toast.success(t('mockups.filter-unassigned-success'));
      await onRefetch();
    } catch (error) {
      console.error('Error un-assigning filter:', error);
      toast.error(t('mockups.filter-unassigned-error'));
    }
  };

  return (
    <div className="space-y-2">
      {!isDeleted && (
        <Select
          value={selectedFilterToAssign}
          onValueChange={(value) => {
            if (value) {
              handleAssignFilter(value);
              setSelectedFilterToAssign('');
            }
          }}
        >
          <SelectTrigger className="w-full h-10 text-sm">
            <SelectValue placeholder={t('mockups.assign-filter-placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {availableFilters
              ?.filter((filter: MockFilter) => !mockFilters?.some((f) => f.id === filter.id))
              .map((filter: MockFilter) => (
                <SelectItem key={filter.id} value={filter.id}>
                  {filter.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )}

      <div className="flex flex-wrap gap-2">
        {mockFilters?.map((filter) => (
          <div key={filter.id} className="flex items-center gap-1 bg-white px-3 py-2 rounded-md border">
            <Tag className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">{filter.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleUnassignFilter(filter.id)}
              className="h-5 w-5 p-0 text-black hover:text-gray-700 hover:bg-gray-100"
              disabled={isDeleted}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MockFiltersCell;
